import { useId, useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { analyze, type ReadingBand, readingBand } from "../lib/readability";

const DEFAULT_TEXT =
  "Readability scores estimate how hard your text is to read. Shorter sentences and simpler words usually score higher. Paste your own writing to see how it compares.";

const BAND_LABEL: Record<ReadingBand, () => string> = {
  "very-easy": () => m.ra_band_very_easy(),
  easy: () => m.ra_band_easy(),
  "fairly-easy": () => m.ra_band_fairly_easy(),
  standard: () => m.ra_band_standard(),
  "fairly-difficult": () => m.ra_band_fairly_difficult(),
  difficult: () => m.ra_band_difficult(),
  "very-difficult": () => m.ra_band_very_difficult(),
};

function MetricCard({
  label,
  value,
  testid,
  emphasis,
}: {
  label: string;
  value: string;
  testid: string;
  emphasis?: boolean;
}) {
  return (
    <div className="rounded-xl border border-border bg-card/60 p-4">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        data-testid={testid}
        className={`mt-1 font-mono ${emphasis ? "text-2xl font-bold" : "text-xl font-semibold"}`}
      >
        {value}
      </div>
    </div>
  );
}

export function ReadabilityTool() {
  const uid = useId();
  const [text, setText] = useState(DEFAULT_TEXT);

  const result = useMemo(() => analyze(text), [text]);
  const bandKey = readingBand(result.fleschReadingEase);
  const bandLabel = Object.hasOwn(BAND_LABEL, bandKey)
    ? BAND_LABEL[bandKey]()
    : "";

  const metrics: Array<{
    id: string;
    label: string;
    value: string;
    testid: string;
  }> = [
    {
      id: "fre",
      label: m.ra_metric_fre(),
      value: result.fleschReadingEase.toFixed(1),
      testid: "ra-fre",
    },
    {
      id: "grade",
      label: m.ra_metric_grade(),
      value: result.fleschKincaidGrade.toFixed(1),
      testid: "ra-grade",
    },
    {
      id: "words",
      label: m.ra_metric_words(),
      value: String(result.words),
      testid: "ra-words",
    },
    {
      id: "sentences",
      label: m.ra_metric_sentences(),
      value: String(result.sentences),
      testid: "ra-sentences",
    },
    {
      id: "syllables",
      label: m.ra_metric_syllables(),
      value: String(result.syllables),
      testid: "ra-syllables",
    },
    {
      id: "characters",
      label: m.ra_metric_characters(),
      value: String(result.characters),
      testid: "ra-characters",
    },
    {
      id: "time",
      label: m.ra_metric_time(),
      value: `${result.readingTimeMin.toFixed(1)} ${m.ra_time_unit()}`,
      testid: "ra-time",
    },
  ];

  return (
    <div className="space-y-6">
      <div className="space-y-1.5">
        <Label htmlFor={`${uid}-text`}>{m.ra_input_label()}</Label>
        <textarea
          id={`${uid}-text`}
          value={text}
          onChange={(e) => setText(e.target.value)}
          className="min-h-[180px] w-full rounded-md border border-border bg-card px-3 py-2 text-sm leading-relaxed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      <div className="rounded-2xl border border-border bg-card/60 p-4 sm:p-6">
        <div className="text-xs uppercase tracking-wide text-muted-foreground">
          {m.ra_band_label()}
        </div>
        <div data-testid="ra-band" className="mt-1 text-2xl font-bold">
          {bandLabel}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {metrics.map((metric) => (
          <MetricCard
            key={metric.id}
            label={metric.label}
            value={metric.value}
            testid={metric.testid}
            emphasis={metric.id === "fre"}
          />
        ))}
      </div>
    </div>
  );
}
